import * as React from 'react';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import {
  Checkbox,
  CircularProgress,
  FormControl,
  FormLabel,
  SxProps,
} from '@mui/material';
import { t } from 'i18next';
import { BLUE_GRAY_200, PRIMARY } from '../../constants/Colors';
import { Fragment, useEffect, useState } from 'react';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import i18n from '../../languages/languages';

const filter = createFilterOptions<any>();

interface BakeasySelectAutoCompleteProps {
  key?: any;
  inputValue?: string;
  options?: any;
  value?: string;
  values?: string[] | undefined;
  label?: string;
  disabled?: boolean;
  multiple?: boolean;
  onChange: (newValue: any) => void;
  onAddClick?: (props: any) => void;
  required?: any;
  placeholder?: any;
}

export const autoCompleteSxProps: SxProps = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: BLUE_GRAY_200, borderRadius: 2 },
    '&:hover fieldset': { borderColor: BLUE_GRAY_200 },
    '&.Mui-focused fieldset': { borderColor: PRIMARY, borderWidth: 1.5 },
  },
  '& .MuiInputBase-input::placeholder': { fontSize: '0.9rem' },
};

export function BakeasySelectAutoCompleteCategory(
  props: BakeasySelectAutoCompleteProps
) {
  const [value, setValue] = React.useState<any | null>(null);
  const [open, toggleOpen] = React.useState(false);

  const handleClose = () => {
    setDialogValue({ title: '', year: '' });
    toggleOpen(false);
  };

  const [dialogValue, setDialogValue] = React.useState({ title: '', year: '' });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setValue({
      title: dialogValue.title,
      year: parseInt(dialogValue.year, 10),
    });
    handleClose();
  };

  return (
    <React.Fragment>
      <Autocomplete
        disabled={props?.disabled ?? false}
        value={value}
        onInputChange={(event: any, value: any) => {
          if (typeof value === 'string' && value?.length > 2) {
            props?.onChange(value);
          }
        }}
        onChange={(event, newValue) => {
          props?.onChange(newValue);
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);

          if (params.inputValue !== '') {
            filtered.push({
              inputValue: params.inputValue,
              categoryName: `Add "${params.inputValue}"`,
            });
          }

          return filtered;
        }}
        id="free-solo-dialog-demo"
        options={props?.options ?? []}
        getOptionLabel={(option: any) => {
          // for example value selected with enter, right from the input
          if (typeof option === 'string') {
            return option;
          }

          if (option.categoryName) {
            return option.categoryName;
          }

          if (option.inputValue) {
            return option.inputValue;
          }

          return option;
        }}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        renderOption={(props, option) => (
          <li {...props}>{option.categoryName}</li>
        )}
        sx={{ width: 300 }}
        freeSolo
        renderInput={(params) => (
          <TextField {...params} label="Free solo dialog" />
        )}
      />
      <Dialog open={open} onClose={handleClose}>
        <form onSubmit={handleSubmit}>
          <DialogTitle>Add a new film</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Did you miss any film in our list? Please, add it!
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="name"
              value={dialogValue.title}
              onChange={(event) =>
                setDialogValue({ ...dialogValue, title: event.target.value })
              }
              label="title"
              type="text"
              variant="standard"
            />
            <TextField
              margin="dense"
              id="name"
              value={dialogValue.year}
              onChange={(event) =>
                setDialogValue({ ...dialogValue, year: event.target.value })
              }
              label="year"
              type="number"
              variant="standard"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit">Add</Button>
          </DialogActions>
        </form>
      </Dialog>
    </React.Fragment>
  );
}

function sleep(duration: number): Promise<void> {
  return new Promise<void>((resolve) => {
    setTimeout(() => {
      resolve();
    }, duration);
  });
}

const icon = <CheckBoxOutlineBlankIcon />;
const checkedIcon = <CheckBoxIcon />;
export function BakeasyAutoCompleteMessagesTo(
  props: BakeasySelectAutoCompleteProps
) {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<any>(props?.options);
  const loading = open && options?.length === 0;

  React.useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      await sleep(1e3 / 2); // For demo purposes.

      if (active) {
        setOptions(props?.options);
      }
    })();

    return () => {
      active = false;
    };
  }, [loading]);

  React.useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  React.useEffect(() => {
    if (props?.options && props?.options?.length > 0) {
      setOptions(props?.options);
    }
  }, [props?.options]);

  return (
    <React.Fragment>
      <FormControl sx={{ minWidth: 120 }} fullWidth={true}>
        <FormLabel sx={{ fontWeight: 600, fontSize: 15, marginBottom: 0.5 }}>
          {props?.label ? t(props.label) : t('TO')}
          {' *'}
        </FormLabel>
        <Autocomplete
          multiple={props?.multiple ?? false}
          size="small"
          value={props?.multiple ? props?.values : props?.value}
          disabled={props?.disabled ?? false}
          sx={autoCompleteSxProps}
          id="asynchronous-demo"
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          isOptionEqualToValue={(option: any, value: any) => {
            return option?.name === value?.name;
          }}
          getOptionLabel={(option: any) => {
            return option?.name;
          }}
          options={options}
          disableCloseOnSelect
          loading={loading}
          renderOption={(props, option: any, { selected }) => {
            return (
              <li {...props} style={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ padding: 0 }}
                  checked={selected}
                  size="small"
                />
                {/* {option?.name} */}
                <span style={{ marginLeft: '8px' }}>{option?.name}</span>
              </li>
            );
          }}
          // renderInput={(params: any) => {
          //   return (
          //     <TextField
          //       {...params}
          //       placeholder={t('SELECT_TO')}
          //       InputProps={{
          //         endAdornment: (
          //           <React.Fragment>
          //             {loading ? <CircularProgress color="inherit" size={20} /> : null}
          //             {params.InputProps.endAdornment}
          //           </React.Fragment>
          //         )
          //       }}
          //     />
          //   );
          // }}
          renderInput={(params) => (
            <TextField {...params} placeholder={t('SELECT_TO')} />
          )}
          onInputChange={(event: any, value: any) => {
            if (typeof value === 'string' && value?.length > 2) {
              props?.onChange(value);
            }

            if (typeof value === 'string' && value?.length === 0) {
              props?.onChange(value);
            }
          }}
          onChange={(event, newValue) => {
            if (typeof newValue === 'object') {
              props?.onChange(newValue);
            }
          }}
        />
      </FormControl>
    </React.Fragment>
  );
}

export function BakeasyAutoCompleteCategoryA(
  props: BakeasySelectAutoCompleteProps
) {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<any>(props?.options);
  const loading = open && options.length === 0;

  React.useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      await sleep(1e3); // For demo purposes.

      if (active) {
        setOptions(props?.options);
      }
    })();

    return () => {
      active = false;
    };
  }, [loading]);

  React.useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  React.useEffect(() => {
    if (props?.options && props?.options?.length > 0) {
      setOptions(props?.options);
    }
  }, [props?.options]);

  return (
    <React.Fragment>
      <FormControl sx={{ minWidth: 120 }} fullWidth={true}>
        <FormLabel sx={{ fontWeight: 600, fontSize: 15, marginBottom: 0.5 }}>
          {props?.label ? t(props.label) : t('CATEGORY')}
          {props?.required ? ' *' : ''}
        </FormLabel>
        <Autocomplete
          size="small"
          value={props.value || null}
          disabled={props?.disabled ?? false}
          sx={autoCompleteSxProps}
          id="asynchronous-demo"
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          isOptionEqualToValue={(option: any, value: any) => {
            return option?.categoryName === value?.categoryName;
          }}
          getOptionLabel={(option: any) => {
            return option?.categoryName;
          }}
          options={options}
          loading={loading}
          renderInput={(params: any) => {
            return (
              <TextField
                {...params}
                placeholder={t('SELECT_CATEGORY')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            );
          }}
          onInputChange={(event: any, value: any) => {
            if (
              typeof value === 'string' &&
              (value?.length > 2 || value?.length === 0)
            ) {
              props?.onChange(value);
            }
          }}
          onChange={(event, newValue) => {
            if (typeof newValue === 'object') {
              props?.onChange(newValue);
            }
          }}
        />
      </FormControl>
    </React.Fragment>
  );
}

export function BakeasyAutoCompleteSubCategoryA(
  props: BakeasySelectAutoCompleteProps
) {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<any>(props?.options);
  const loading = false; // && options?.length === 0;

  React.useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      await sleep(1e3); // For demo purposes.
      if (active) {
        setOptions(props?.options);
      }
    })();

    return () => {
      active = false;
    };
  }, [loading]);

  React.useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  React.useEffect(() => {
    if (props?.options && props?.options?.length > 0) {
      setOptions(props?.options);
    }
  }, [props?.options]);

  return (
    <FormControl sx={{ minWidth: 120 }} fullWidth={true} disabled={false}>
      <FormLabel sx={{ fontWeight: 600, fontSize: 15, marginBottom: 0.5 }}>
        {props?.label ? t(props.label) : t('SUBCATEGORY')}
        {props?.required ? ' *' : ''}
      </FormLabel>
      <Autocomplete
        size="small"
        value={props.value || null}
        disabled={props?.disabled ?? false}
        sx={autoCompleteSxProps}
        id="asynchronous-demo"
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        isOptionEqualToValue={(option: any, value: any) => {
          return option?.subCategoryName === value?.subCategoryName;
        }}
        getOptionLabel={(option: any) => {
          return option?.subCategoryName;
        }}
        options={options}
        loading={loading}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              placeholder={t('SELECT_SUB_CATEGORY')}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          );
        }}
        onInputChange={(event: any, value: any) => {
          if (
            typeof value === 'string' &&
            (value?.length > 2 || value?.length === 0)
          ) {
            props?.onChange(value);
          }
        }}
        onChange={(event, newValue) => {
          if (typeof newValue === 'object') {
            props?.onChange(newValue);
          }
        }}
      />
    </FormControl>
  );
}

export function BakeasyAutoCompleteIngredient(
  props: BakeasySelectAutoCompleteProps
) {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<any>(null);
  const loading = open && options.length === 0;

  let newIngredient: any = { id: 0, name: t('ADD_NEW_INGREDIENT') };

  function setOption() {
    let newOptions: {}[] =
      props?.options && props?.options?.length > 0 ? props?.options : [];
    setOptions(newOptions);
  }

  React.useEffect(() => {
    setOption();
  }, [props?.options]);

  return (
    <React.Fragment>
      <FormControl sx={{ minWidth: 120 }} fullWidth={true}>
        <FormLabel sx={{ fontWeight: 600, fontSize: 15, marginBottom: 0.5 }}>
          {props?.label ? t(props.label) : t('INGREDIENT')}
          {' *'}
        </FormLabel>
        <Autocomplete
          size="small"
          value={props?.value || null}
          disabled={props?.disabled ?? false}
          sx={autoCompleteSxProps}
          id="asynchronous-demo"
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);
            if (!filtered?.includes(newIngredient)) {
              //filtered.push(newIngredient);
              filtered.unshift(newIngredient);
            }

            return filtered;
          }}
          isOptionEqualToValue={(option: any, value: any) => {
            return option?.ingredientName === value?.ingredientName;
          }}
          getOptionLabel={(option: any) => {
            if (option?.ingredientName) {
              return option?.ingredientName;
            }
            if (option?.name) {
              return option?.name;
            }
          }}
          options={options}
          loading={loading}
          renderInput={(params: any) => {
            return (
              <TextField
                {...params}
                placeholder={t('SELECT_INGREDIENT')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            );
          }}
          onInputChange={(event: any, value: any) => {
            if (typeof value === 'string' && value?.length > 1) {
              props?.onChange(value);
            }

            if (typeof value === 'string' && value?.length === 0) {
              props?.onChange(value);
            }
          }}
          onChange={(event, newValue: any) => {
            if (
              typeof newValue === 'object' &&
              newValue?.id === 0 &&
              newValue?.name === t('ADD_NEW_INGREDIENT')
            ) {
              if (props?.onAddClick) {
                props?.onAddClick(newValue);
              }
              return;
            }

            if (typeof newValue === 'object') {
              props?.onChange(newValue);
            }
          }}
        />
      </FormControl>
    </React.Fragment>
  );
}

export function BakeasySelectAutoCompleteStates(
  props: BakeasySelectAutoCompleteProps
) {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<any>(props?.options ?? []);
  const loading = open && options?.length === 0;

  React.useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      await sleep(1e3); // For demo purposes.

      if (active) {
        setOptions(props?.options);
      }
    })();

    return () => {
      active = false;
    };
  }, [loading]);

  React.useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  React.useEffect(() => {
    if (props?.options && props?.options?.length > 0) {
      setOptions(props?.options);
    }
  }, [props?.options]);

  return (
    <React.Fragment>
      <FormControl sx={{ minWidth: 120 }} fullWidth={true}>
        <FormLabel sx={{ fontWeight: 600, fontSize: 15, marginBottom: 0.5 }}>
          {props?.label ? t(props.label) : t('STATE')}
          {/* {' *'} */}
        </FormLabel>
        <Autocomplete
          size="small"
          value={props.value || null}
          disabled={props?.disabled ?? false}
          sx={autoCompleteSxProps}
          id="asynchronous-demo"
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          isOptionEqualToValue={(option: any, value: any) => {
            if (typeof option === 'string') {
              return true;
            }
            return option?.stateName === value?.stateName;
          }}
          // getOptionLabel={(option: any) => {
          //   if (typeof option === 'string') {
          //     return option;
          //   }
          //   return option?.stateName;
          // }}
          // options={options ?? []}
          getOptionLabel={(option: any) => {
            if (!option) return '';
            const key = `${option?.toUpperCase()?.replace(/\s+/g, '_')}`;
            const translation = t(key);
            return translation === key ? option : translation;
          }}
          options={options}
          loading={loading}
          renderInput={(params: any) => {
            return (
              <TextField
                {...params}
                placeholder={t('SELECT_STATE')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            );
          }}
          onInputChange={(event: any, value: any) => {
            props?.onChange(value);
          }}
          onChange={(event, newValue) => {
            props?.onChange(newValue);
          }}
        />
      </FormControl>
    </React.Fragment>
  );
}

export function BakeasySelectAutoCompleteCities(
  props: BakeasySelectAutoCompleteProps
) {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<any>(props?.options ?? []);
  const loading = open && options?.length === 0;

  React.useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      await sleep(1e3); // For demo purposes.

      if (active) {
        setOptions(props?.options);
      }
    })();

    return () => {
      active = false;
    };
  }, [loading]);

  React.useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  React.useEffect(() => {
    if (props?.options && props?.options?.length > 0) {
      setOptions(props?.options);
    }
  }, [props?.options]);

  return (
    <React.Fragment>
      <FormControl sx={{ minWidth: 120 }} fullWidth={true}>
        <FormLabel sx={{ fontWeight: 600, fontSize: 15, marginBottom: 0.5 }}>
          {props?.label ? t(props.label) : t('CITY')}
          {/* {' *'} */}
        </FormLabel>
        <Autocomplete
          size="small"
          value={props.value || null}
          disabled={props?.disabled ?? false}
          sx={autoCompleteSxProps}
          id="asynchronous-demo"
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          isOptionEqualToValue={(option: any, value: any) => {
            if (typeof option === 'string') {
              return true;
            }
            return option?.cityName === value?.cityName;
          }}
          getOptionLabel={(option: any) => {
            if (typeof option === 'string') {
              return option;
            }
            return option?.cityName;
          }}
          options={options ?? []}
          loading={loading}
          renderInput={(params: any) => {
            return (
              <TextField
                {...params}
                placeholder={t('SELECT_CITY')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            );
          }}
          onInputChange={(event: any, value: any) => {
            props?.onChange(value);
          }}
          onChange={(event, newValue) => {
            props?.onChange(newValue);
          }}
        />
      </FormControl>
    </React.Fragment>
  );
}

export function BakeasySelectAutoCompleteZipcode(
  props: BakeasySelectAutoCompleteProps
) {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<any>(
    props?.options ? props?.options : []
  );
  const loading = open && options?.length === 0;

  React.useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      await sleep(1e3); // For demo purposes.

      if (active) {
        setOptions(props?.options ? props?.options : []);
      }
    })();

    return () => {
      active = false;
    };
  }, [loading]);

  React.useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  React.useEffect(() => {
    if (props?.options && props?.options?.length > 0) {
      setOptions(props?.options);
    }
  }, [props?.options]);

  return (
    <React.Fragment>
      <FormControl sx={{ minWidth: 120 }} fullWidth={true}>
        <FormLabel sx={{ fontWeight: 600, fontSize: 15, marginBottom: 0.5 }}>
          {props?.label ? t(props.label) : t('POSTAL_CODE')}
          {/* {' *'} */}
        </FormLabel>
        <Autocomplete
          size="small"
          value={props.value || null}
          disabled={props?.disabled ?? false}
          sx={autoCompleteSxProps}
          id="asynchronous-demo"
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          isOptionEqualToValue={(option: any, value: any) => {
            if (typeof option === 'string') {
              return true;
            }
            return option?.cityName === value?.cityName;
          }}
          getOptionLabel={(option: any) => {
            if (typeof option === 'string') {
              return option;
            }
            return option?.cityName;
          }}
          options={options ?? []}
          loading={loading}
          renderInput={(params: any) => {
            return (
              <TextField
                {...params}
                placeholder={t('SELECT_POSTAL_CODE')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            );
          }}
          onInputChange={(event: any, value: any) => {
            props?.onChange(value);
          }}
          onChange={(event, newValue) => {
            props?.onChange(newValue);
          }}
        />
      </FormControl>
    </React.Fragment>
  );
}

export function BakeasyAutoCompleteCategory(
  props: BakeasySelectAutoCompleteProps
) {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<any>(
    props?.options ? props?.options : []
  );
  const loading = open && options?.length === 0;

  React.useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      await sleep(1e3); // For demo purposes.

      if (active) {
        setOptions(props?.options ? props?.options : []);
      }
    })();

    return () => {
      active = false;
    };
  }, [loading]);

  React.useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  React.useEffect(() => {
    if (props?.options && props?.options?.length > 0) {
      setOptions(props?.options);
    }
  }, [props?.options]);

  return (
    <React.Fragment>
      <FormControl sx={{ minWidth: 120 }} fullWidth={true}>
        <FormLabel sx={{ fontWeight: 600, fontSize: 15, marginBottom: 0.5 }}>
          {props?.label ? t(props.label) : t('CATEGORY')}
          {/* {' *'} */}
        </FormLabel>
        <Autocomplete
          size="small"
          value={props.value || null}
          disabled={props?.disabled ?? false}
          sx={autoCompleteSxProps}
          id="asynchronous-demo"
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          isOptionEqualToValue={(option: any, value: any) => {
            if (typeof option === 'string') {
              return true;
            }

            return option?.cityName === value?.cityName;
          }}
          getOptionLabel={(option: any) => {
            if (typeof option === 'string') {
              return option;
            }
            return option?.cityName;
          }}
          options={options ?? []}
          loading={loading}
          renderInput={(params: any) => {
            return (
              <TextField
                {...params}
                placeholder={t('SELECT_CATEGORY')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            );
          }}
          onInputChange={(event: any, value: any) => {
            props?.onChange(value);
          }}
          onChange={(event, newValue) => {
            props?.onChange(newValue);
          }}
        />
      </FormControl>
    </React.Fragment>
  );
}

export function BakeasyAutoCompleteSubCategory(
  props: BakeasySelectAutoCompleteProps
) {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<any>(
    props?.options ? props?.options : []
  );
  const loading = open && options?.length === 0;

  React.useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      await sleep(1e3); // For demo purposes.

      if (active) {
        setOptions(props?.options ? props?.options : []);
      }
    })();

    return () => {
      active = false;
    };
  }, [loading]);

  React.useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  React.useEffect(() => {
    if (props?.options && props?.options?.length > 0) {
      setOptions(props?.options);
    }
  }, [props?.options]);

  return (
    <React.Fragment>
      <FormControl sx={{ minWidth: 120 }} fullWidth={true}>
        <FormLabel sx={{ fontWeight: 600, fontSize: 15, marginBottom: 0.5 }}>
          {props?.label ? t(props.label) : t('SUB_CATEGORY')}
          {/* {' *'} */}
        </FormLabel>
        <Autocomplete
          size="small"
          value={props.value || null}
          disabled={props?.disabled ?? false}
          sx={autoCompleteSxProps}
          id="asynchronous-demo"
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          isOptionEqualToValue={(option: any, value: any) => {
            if (typeof option === 'string') {
              return true;
            }
            return option?.cityName === value?.cityName;
          }}
          getOptionLabel={(option: any) => {
            if (typeof option === 'string') {
              return option;
            }
            return option?.cityName;
          }}
          options={options ?? []}
          loading={loading}
          renderInput={(params: any) => {
            return (
              <TextField
                {...params}
                placeholder={t('SELECT_SUB_CATEGORY')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            );
          }}
          onInputChange={(event: any, value: any) => {
            props?.onChange(value);
          }}
          onChange={(event, newValue) => {
            props?.onChange(newValue);
          }}
        />
      </FormControl>
    </React.Fragment>
  );
}

export function BakeasySelectAutoCompleteMessageFrom(
  props: BakeasySelectAutoCompleteProps
) {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<any>(props?.options);
  const loading = open && options?.length === 0;

  React.useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      await sleep(1e3); // For demo purposes.

      if (active) {
        setOptions(props?.options);
      }
    })();

    return () => {
      active = false;
    };
  }, [loading]);

  React.useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  React.useEffect(() => {
    if (props?.options && props?.options?.length > 0) {
      setOptions(props?.options);
    }
  }, [props?.options]);

  return (
    <React.Fragment>
      <FormControl sx={{ minWidth: 120 }} fullWidth={true}>
        <FormLabel sx={{ fontWeight: 600, fontSize: 15, marginBottom: 0.5 }}>
          {props?.label ? t(props?.label) : t('FROM')}
          {/* {' *'} */}
        </FormLabel>
        <Autocomplete
          multiple={props?.multiple ?? false}
          size="small"
          value={props?.multiple ? props?.values : props?.value}
          disabled={props?.disabled ?? false}
          sx={autoCompleteSxProps}
          id="asynchronous-demo"
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          isOptionEqualToValue={(option: any, value: any) => {
            if (typeof option === 'string') {
              return true;
            }
            return option?.name === value?.name;
          }}
          getOptionLabel={(option: any) => {
            if (typeof option === 'string') {
              return option;
            }
            return option?.name;
          }}
          options={options}
          loading={loading}
          renderInput={(params: any) => {
            return (
              <TextField
                {...params}
                placeholder={t('SELECT_FROM')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            );
          }}
          onInputChange={(event: any, value: any) => {
            if (
              typeof value === 'string' &&
              (value?.length > 2 || value?.length === 0)
            ) {
              props?.onChange(value);
            }
          }}
          onChange={(event, newValue) => {
            if (typeof newValue === 'object') {
              props?.onChange(newValue);
            }
          }}
        />
      </FormControl>
    </React.Fragment>
  );
}

export function BakeasySelectAutoCompleteMessageTo(
  props: BakeasySelectAutoCompleteProps
) {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<any>(props?.options);
  const loading = open && options?.length === 0;

  React.useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      await sleep(1e3); // For demo purposes.

      if (active) {
        setOptions(props?.options);
      }
    })();

    return () => {
      active = false;
    };
  }, [loading]);

  React.useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  React.useEffect(() => {
    if (props?.options && props?.options?.length > 0) {
      setOptions(props?.options);
    }
  }, [props?.options]);

  return (
    <React.Fragment>
      <FormControl sx={{ minWidth: 120 }} fullWidth={true}>
        <FormLabel sx={{ fontWeight: 600, fontSize: 15, marginBottom: 0.5 }}>
          {props?.label ? t(props?.label) : t('TO')}
          {/* {' *'} */}
        </FormLabel>
        <Autocomplete
          multiple={props?.multiple ?? false}
          size="small"
          value={props?.multiple ? props?.values : props?.value}
          disabled={props?.disabled ?? false}
          sx={autoCompleteSxProps}
          id="asynchronous-demo"
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          isOptionEqualToValue={(option: any, value: any) => {
            if (typeof option === 'string') {
              return true;
            }
            return option?.name === value?.name;
          }}
          getOptionLabel={(option: any) => {
            if (typeof option === 'string') {
              return option;
            }
            return option?.name;
          }}
          options={options}
          loading={loading}
          renderInput={(params: any) => {
            return (
              <TextField
                {...params}
                placeholder={t('SELECT_TO')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            );
          }}
          onInputChange={(event: any, value: any) => {
            if (
              typeof value === 'string' &&
              (value?.length > 2 || value?.length === 0)
            ) {
              props?.onChange(value);
            }
          }}
          onChange={(event, newValue) => {
            if (typeof newValue === 'object') {
              props?.onChange(newValue);
            }
          }}
        />
      </FormControl>
    </React.Fragment>
  );
}

export function BakeasyAutoCompleteProductName(
  props: BakeasySelectAutoCompleteProps
) {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<any>(
    props?.options ? props?.options : []
  );
  const loading = open && options?.length === 0;

  React.useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      await sleep(1e3); // For demo purposes.

      if (active) {
        setOptions(props?.options ? props?.options : []);
      }
    })();

    return () => {
      active = false;
    };
  }, [loading]);

  React.useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  React.useEffect(() => {
    if (props?.options && props?.options?.length > 0) {
      setOptions(props?.options);
    }
  }, [props?.options]);

  return (
    <React.Fragment>
      <FormControl sx={{ minWidth: 120 }} fullWidth={true}>
        <FormLabel sx={{ fontWeight: 600, fontSize: 15, marginBottom: 0.5 }}>
          {props?.label ? t(props.label) : t('PRODUCT_NAME')}
          {' *'}
        </FormLabel>
        <Autocomplete
          id={props?.label ? t(props.label) : t('PRODUCT_NAME')}
          size="small"
          value={props.value || null}
          disabled={props?.disabled ?? false}
          noOptionsText={''}
          sx={autoCompleteSxProps}
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          isOptionEqualToValue={(option: any, value: any) => {
            if (typeof option === 'string') {
              return true;
            }

            return option?.cityName === value?.cityName;
          }}
          getOptionLabel={(option: any) => {
            if (typeof option === 'string') {
              return option;
            }
            return option?.cityName;
          }}
          options={options ?? []}
          loading={loading}
          renderInput={(params: any) => {
            return (
              <TextField
                {...params}
                placeholder={
                  props.placeholder ? props?.placeholder : t(props?.label || '')
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            );
          }}
          onInputChange={(event: any, value: any) => {
            props?.onChange(value);
          }}
          onChange={(event, newValue) => {
            props?.onChange(newValue);
          }}
        />
      </FormControl>
    </React.Fragment>
  );
}

export function BakeasyAutoCompleteProduct(
  props: BakeasySelectAutoCompleteProps
) {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<any>(
    props?.options ? props?.options : []
  );
  const loading = open && options.length === 0;

  React.useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      await sleep(1e3); // For demo purposes.
      if (active) {
        setOptions(props?.options ? props?.options : []);
      }
    })();

    return () => {
      active = false;
    };
  }, [loading]);

  React.useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  React.useEffect(() => {
    if (props?.options && props?.options?.length > 0) {
      setOptions(props?.options);
    }
  }, [props?.options]);

  return (
    <FormControl sx={{ minWidth: 120 }} fullWidth={true} disabled={false}>
      <FormLabel sx={{ fontWeight: 600, fontSize: 15, marginBottom: 0.5 }}>
        {props?.label ? t(props.label) : t('PRODUCT')}
        {props?.required ? ' *' : ''}
      </FormLabel>
      <Autocomplete
        size="small"
        value={props.value || null}
        disabled={props?.disabled ?? false}
        sx={autoCompleteSxProps}
        id="asynchronous-demo"
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        isOptionEqualToValue={(option: any, value: any) => {
          if (typeof option === 'string') {
            return true;
          }
          return option?.productName === value?.productName;
        }}
        getOptionLabel={(option: any) => {
          if (typeof option === 'string' || option === null) {
            return option;
          }
          return option.productName || 'No data';
        }}
        options={options ?? []}
        loading={loading}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              placeholder={t('SELECT_PRODUCT')}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          );
        }}
        onInputChange={(event: any, value: any) => {
          if (typeof value === 'string' && value?.length > 2) {
            props?.onChange(value);
          }
        }}
        onChange={(event, newValue) => {
          if (typeof newValue === 'object') {
            props?.onChange(newValue);
          }
        }}
      />
    </FormControl>
  );
}

export function BakeasyAutoCompleteTax(props: BakeasySelectAutoCompleteProps) {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<any>(
    props?.options ? props?.options : []
  );
  const loading = open && options?.length === 0;

  let newTax: any = { id: 0, name: t('ADD_NEW_TAX'), namee: 'Add New Tax' };
  let na: any = { id: 1, taxName: t('NOT_APPLICABLE') };

  function setOption() {
    let newOptions: {}[] =
      props?.options && props?.options?.length > 0 ? props?.options : [];
    setOptions(newOptions);
  }

  React.useEffect(() => {
    setOption();
  }, [props?.options]);

  return (
    <React.Fragment>
      <FormControl sx={{ minWidth: 120 }} fullWidth={true}>
        <FormLabel sx={{ fontWeight: 600, fontSize: 15, marginBottom: 0.5 }}>
          {props?.label ? t(props?.label) : ''}
          {props?.required ? ' *' : ''}
        </FormLabel>
        <Autocomplete
          size="small"
          value={props?.value || null}
          disabled={props?.disabled ?? false}
          sx={autoCompleteSxProps}
          id="asynchronous-demo"
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);
            if (!filtered?.includes(newTax)) {
              filtered.push(newTax);
            }
            if (!filtered?.includes(na)) {
              filtered.push(na);
            }

            return filtered;
          }}
          isOptionEqualToValue={(option: any, value: any) => {
            if (typeof value === 'string' && value?.length !== 0) {
              return true;
            }

            return option?.taxName === value?.taxName;
          }}
          getOptionLabel={(option: any) => {
            if (option?.taxName) {
              return option?.taxName;
            }
            if (option?.name) {
              return option?.name;
            }
          }}
          options={options}
          loading={loading}
          renderInput={(params: any) => {
            return (
              <TextField
                {...params}
                placeholder={
                  props.placeholder ? props?.placeholder : t(props?.label || '')
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            );
          }}
          onInputChange={(event: any, value: any) => {
            if (typeof value === 'string' && value?.length > 0) {
              props?.onChange(value);
            }

            if (typeof value === 'string' && value?.length === 0) {
              props?.onChange(value);
            }
          }}
          onChange={(event, newValue: any) => {
            if (
              typeof newValue === 'object' &&
              newValue?.id === 0 &&
              newValue?.namee === 'Add New Tax'
            ) {
              if (props?.onAddClick) {
                props?.onAddClick(newValue);
              }
              return;
            }

            if (typeof newValue === 'object') {
              props?.onChange(newValue);
            }
          }}
        />
      </FormControl>
    </React.Fragment>
  );
}

export function BakeasyAutoCompleteShippingMethod(
  props: BakeasySelectAutoCompleteProps
) {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<any>(props?.options ?? []);
  const loading = open && options?.length === 0;

  let newShippingMethod: any = { id: 0, name: 'Add New Shipping Method' };

  function setOption() {
    let newOptions: {}[] =
      props?.options && props?.options?.length > 0 ? props?.options : [];
    setOptions(newOptions);
  }

  React.useEffect(() => {
    setOption();
  }, [props?.options]);

  return (
    <React.Fragment>
      <FormControl sx={{ minWidth: 120 }} fullWidth={true}>
        <FormLabel sx={{ fontWeight: 600, fontSize: 15, marginBottom: 0.5 }}>
          {props?.label ? t(props.label) : t('SHIPPING_METHOD')}
          {props?.required ? ' *' : ''}
        </FormLabel>
        <Autocomplete
          size="small"
          value={props?.value || null}
          disabled={props?.disabled ?? false}
          sx={autoCompleteSxProps}
          id="asynchronous-demo"
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);
            if (!filtered?.includes(newShippingMethod)) {
              filtered.push(newShippingMethod);
            }

            return filtered;
          }}
          isOptionEqualToValue={(option: any, value: any) => {
            if (typeof value === 'string' && value?.length !== 0) {
              return true;
            }

            return option?.methodName === value?.methodName;
          }}
          getOptionLabel={(option: any) => {
            if (option?.methodName) {
              return option?.methodName;
            }
            if (option?.name) {
              return option?.name;
            }
          }}
          options={options}
          loading={loading}
          renderInput={(params: any) => {
            return (
              <TextField
                {...params}
                placeholder={t('SELECT_SHIPPING_METHOD')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            );
          }}
          onInputChange={(event: any, value: any) => {
            if (typeof value === 'string' && value?.length > 2) {
              props?.onChange(value);
            }

            if (typeof value === 'string' && value?.length === 0) {
              props?.onChange(value);
            }
          }}
          onChange={(event, newValue: any) => {
            if (
              typeof newValue === 'object' &&
              newValue?.id === 0 &&
              newValue?.name === 'Add New Shipping Method'
            ) {
              if (props?.onAddClick) {
                props?.onAddClick(newValue);
              }
              return;
            }

            if (typeof newValue === 'object') {
              props?.onChange(newValue);
            }
          }}
        />
      </FormControl>
    </React.Fragment>
  );
}

export function BakeasyAutoCompleteCountry(
  props: BakeasySelectAutoCompleteProps
) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<any>(props?.options ?? []);
  const loading = open && options?.length === 0;

  useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      await sleep(1e3 / 2);

      if (active) {
        setOptions(props?.options);
      }
    })();

    return () => {
      active = false;
    };
  }, [loading]);

  useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  useEffect(() => {
    if (props?.options && props?.options?.length > 0) {
      setOptions(props?.options);
    }
  }, [props?.options]);

  return (
    <Fragment>
      <FormControl sx={{ minWidth: 120 }} fullWidth={true}>
        <FormLabel sx={{ fontWeight: 600, fontSize: 15, marginBottom: 0.5 }}>
          {props?.label ? t(props.label) : t('COUNTRY')}
          {props?.required ? ' *' : ''}
        </FormLabel>
        <Autocomplete
          id={props?.label ? t(props.label) : t('COUNTRY')}
          size="small"
          value={props.value || null}
          disabled={props?.disabled ?? false}
          sx={autoCompleteSxProps}
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          isOptionEqualToValue={(option: any, value: any) => {
            return option?.countryName === value?.countryName;
          }}
          // getOptionLabel={(option: any) => {
          //   return t(option?.countryName);
          // }}
          getOptionLabel={(option: any) => {
            if (!option?.countryName) return '';
            const key = `${option.countryName.toUpperCase().replace(/\s+/g, '_')}`;
            const translation = t(key);
            return translation === key ? option.countryName : translation;
          }}
          options={options}
          loading={loading}
          renderInput={(params: any) => {
            return (
              <TextField
                {...params}
                placeholder={t('SELECT_COUNTRY')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </Fragment>
                  ),
                }}
              />
            );
          }}
          onInputChange={(event: any, value: any) => {
            if (typeof value === 'string' && value?.length > 2) {
              props?.onChange(value);
            }
          }}
          onChange={(event, newValue) => {
            if (typeof newValue === 'object') {
              props?.onChange(newValue);
            }
          }}
        />
      </FormControl>
    </Fragment>
  );
}

export function BakeasyAutoCompleteState(
  props: BakeasySelectAutoCompleteProps
) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<any>(props?.options);
  const loading = open && options?.length === 0;

  useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      await sleep(1e3 / 2);
      if (active) {
        setOptions(props?.options);
      }
    })();

    return () => {
      active = false;
    };
  }, [loading]);

  useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  useEffect(() => {
    if (props?.options && props?.options?.length > 0) {
      setOptions(props?.options);
    }
  }, [props?.options]);

  return (
    <FormControl sx={{ minWidth: 120 }} fullWidth={true} disabled={false}>
      <FormLabel sx={{ fontWeight: 600, fontSize: 15, marginBottom: 0.5 }}>
        {props?.label ? t(props.label) : t('STATE')}
        {/* {' *'} */}
        {props?.required ? ' *' : ''}
      </FormLabel>
      <Autocomplete
        size="small"
        value={props.value || null}
        disabled={props?.disabled ?? false}
        sx={autoCompleteSxProps}
        id={props?.label ? t(props.label) : t('STATE')}
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        isOptionEqualToValue={(option: any, value: any) => {
          return option?.stateName === value?.stateName;
        }}
        // getOptionLabel={(option: any) => {
        //   return option?.stateName;
        // }}
        getOptionLabel={(option: any) => {
          if (!option?.stateName) return '';
          const key = `${option.stateName.toUpperCase().replace(/\s+/g, '_')}`;
          const translation = t(key);
          return translation === key ? option.stateName : translation;
        }}
        options={options}
        loading={loading}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              placeholder={t('SELECT_STATE')}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </Fragment>
                ),
              }}
            />
          );
        }}
        onInputChange={(event: any, value: any) => {
          if (typeof value === 'string' && value?.length > 2) {
            props?.onChange(value);
          }
        }}
        onChange={(event, newValue) => {
          if (typeof newValue === 'object') {
            props?.onChange(newValue);
          }
        }}
      />
    </FormControl>
  );
}

export function BakeasyAutoCompletePaymentMethod(
  props: BakeasySelectAutoCompleteProps
) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<any>(props?.options ?? []);
  const loading = open && options?.length === 0;

  useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      //await sleep(1e3);
      if (active) {
        setOptions(props?.options);
      }
    })();

    return () => {
      active = false;
    };
  }, [loading]);

  useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  useEffect(() => {
    if (props?.options && props?.options?.length > 0) {
      setOptions(props?.options);
    }
  }, [props?.options]);

  return (
    <FormControl sx={{ minWidth: 120 }} fullWidth={true} disabled={false}>
      <FormLabel sx={{ fontWeight: 600, fontSize: 15, marginBottom: 0.5 }}>
        {props?.label ? t(props.label) : t('METHOD')}
        {props?.required ? ' *' : ''}
      </FormLabel>
      <Autocomplete
        size="small"
        value={props.value || null}
        disabled={props?.disabled ?? false}
        sx={autoCompleteSxProps}
        id={props?.label ? t(props.label) : t('METHOD')}
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        // isOptionEqualToValue={(option: any, value: any) => {
        //   return option?.paymentMethod === value?.paymentMethod;
        // }}
        // getOptionLabel={(option: any) => {
        //   return option?.paymentMethod[i18n.language];
        // }}
        isOptionEqualToValue={(option: any, value: any) => {
          return (
            option?.paymentMethod[i18n.language] ===
            value?.paymentMethod[i18n.language]
          );
        }}
        getOptionLabel={(option: any) => {
          return t(option?.paymentMethod[i18n.language]);
        }}
        options={options}
        loading={loading}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              placeholder={t('SELECT_METHOD')}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </Fragment>
                ),
              }}
            />
          );
        }}
        onInputChange={(event: any, value: any) => {
          if (typeof value === 'string' && value?.length > 2) {
            props?.onChange(value);
          }
        }}
        onChange={(event, newValue) => {
          if (typeof newValue === 'object') {
            props?.onChange(newValue);
          }
        }}
      />
    </FormControl>
  );
}

export function BakeasyAutoCompletePaymentTerms(
  props: BakeasySelectAutoCompleteProps
) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<any>(props?.options ?? []);
  const loading = open && options?.length === 0;

  useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      //await sleep(1e3);
      if (active) {
        setOptions(props?.options);
      }
    })();

    return () => {
      active = false;
    };
  }, [loading]);

  useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  useEffect(() => {
    if (props?.options && props?.options?.length > 0) {
      setOptions(props?.options);
    }
  }, [props?.options]);

  return (
    <FormControl sx={{ minWidth: 120 }} fullWidth={true} disabled={false}>
      <FormLabel sx={{ fontWeight: 600, fontSize: 15, marginBottom: 0.5 }}>
        {props?.label ? t(props.label) : t('TERMS')}
        {props?.required ? ' *' : ''}
      </FormLabel>
      <Autocomplete
        size="small"
        value={props.value || null}
        disabled={props?.disabled ?? false}
        sx={autoCompleteSxProps}
        id={props?.label ? t(props.label) : t('TERMS')}
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        isOptionEqualToValue={(option: any, value: any) => {
          return option?.paymentTerms === value?.paymentTerms;
        }}
        getOptionLabel={(option: any) => {
          return option?.paymentTerms[i18n.language];
        }}
        options={options}
        loading={loading}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              placeholder={t('SELECT_TERMS')}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </Fragment>
                ),
              }}
            />
          );
        }}
        onInputChange={(event: any, value: any) => {
          if (typeof value === 'string' && value?.length > 2) {
            props?.onChange(value);
          }
        }}
        onChange={(event, newValue) => {
          if (typeof newValue === 'object') {
            props?.onChange(newValue);
          }
        }}
      />
    </FormControl>
  );
}

export function BakeasyAutoDeliveryTimeframe(
  props: BakeasySelectAutoCompleteProps
) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<any>(props?.options ?? []);
  const loading = open && options?.length === 0;

  useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      if (active) {
        setOptions(props?.options);
      }
    })();

    return () => {
      active = false;
    };
  }, [loading]);

  useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  useEffect(() => {
    if (props?.options && props?.options?.length > 0) {
      setOptions(props?.options);
    }
  }, [props?.options]);

  return (
    <FormControl sx={{ minWidth: 120 }} fullWidth={true} disabled={false}>
      <FormLabel sx={{ fontWeight: 600, fontSize: 15, marginBottom: 0.5 }}>
        {props?.label ? t(props.label) : t('MINIMUM_DELIVERY_TIMEFRAME')}
        {props?.required ? ' *' : ''}
      </FormLabel>
      <Autocomplete
        size="small"
        value={props.value || null}
        disabled={props?.disabled ?? false}
        sx={autoCompleteSxProps}
        id={props?.label ? t(props.label) : t('MINIMUM_DELIVERY_TIMEFRAME')}
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        isOptionEqualToValue={(option: any, value: any) => {
          return option?.timeframe === value?.timeframe;
        }}
        getOptionLabel={(option: any) => {
          return option?.timeframe[i18n.language];
        }}
        options={options}
        loading={loading}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              placeholder={t('SELECT') + t('MINIMUM_DELIVERY_TIMEFRAME')}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </Fragment>
                ),
              }}
            />
          );
        }}
        onInputChange={(event: any, value: any) => {
          if (typeof value === 'string' && value?.length > 2) {
            props?.onChange(value);
          }
        }}
        onChange={(event, newValue) => {
          if (typeof newValue === 'object') {
            props?.onChange(newValue);
          }
        }}
      />
    </FormControl>
  );
}

export function BakeasyAutoUnitOfMeasure(
  props: BakeasySelectAutoCompleteProps
) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<any>(props?.options ?? []);
  const loading = open && options?.length === 0;

  useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      if (active) {
        setOptions(props?.options);
      }
    })();

    return () => {
      active = false;
    };
  }, [loading]);

  useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  useEffect(() => {
    if (props?.options && props?.options?.length > 0) {
      setOptions(props?.options);
    }
  }, [props?.options]);

  return (
    <FormControl sx={{ minWidth: 120 }} fullWidth={true} disabled={false}>
      <FormLabel sx={{ fontWeight: 600, fontSize: 15, marginBottom: 0.5 }}>
        {/* {t('UNIT_OF_MEASURE')} */}
        {props?.label ?? t('UNIT_OF_MEASURE')}
        {props?.required ? ' *' : ''}
      </FormLabel>
      <Autocomplete
        size="small"
        value={props.value || null}
        disabled={props?.disabled ?? false}
        sx={autoCompleteSxProps}
        id={props?.label ? t(props.label) : t('UNIT_OF_MEASURE')}
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        isOptionEqualToValue={(option: any, value: any) => {
          return option?.measureType === value?.measureType;
        }}
        getOptionLabel={(option: any) => {
          return option?.measureType[i18n.language];
        }}
        options={options}
        loading={loading}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              placeholder={t('ENTER_UNIT_OF_MEASURE')}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </Fragment>
                ),
              }}
            />
          );
        }}
        onInputChange={(event: any, value: any) => {
          if (typeof value === 'string' && value?.length > 2) {
            props?.onChange(value);
          }
        }}
        onChange={(event, newValue) => {
          if (typeof newValue === 'object') {
            props?.onChange(newValue);
          }
        }}
      />
    </FormControl>
  );
}
